/**
 * Type Stage — Stage 1b: Question type validation.
 *
 * ARCHITECTURE v4.0 — Phase 2, Step 2.2
 *
 * Validates:
 * - questionType is a known type in the registry
 * - Interactive/specialized types have required data.{type} structure
 * - Type-specific data field values (ranges, arrays, consistency)
 * - data.{type} convention compliance (no interactiveConfig)
 *
 * Runs after Schema Stage (1) and before Graph Stage (2).
 * Issues use domain 'type', stage 1 (as "Stage 1b").
 *
 * Refactored 2026-04-30: Split from 2786-line god-file into focused validator modules.
 * Validators grouped by domain in typeValidators/*.
 */

import type { ValidationIssue, PipelineStage } from '../issueContract'
import { blocker } from '../issueContract'
import {
  QUESTION_TYPE_REGISTRY,
  type QuestionTypeDefinition,
} from '../../questionTypeDefinitions'
import type { Question, QuestionType } from '../../../content'

// ─── Validator Imports ────────────────────────────────────────────────────────

import {
  validateClock,
  validateMoney,
  validateColumnArithmetic,
  validateRatioTable,
  validateNumberSequence,
  validateGridShade,
  validateStepSolver,
  validateComparisonJudge,
  validateMultiplicationGrid,
  validateEstimationSlider,
  validateThermometerSlider,
} from './typeValidators/mathValidators'

import {
  validateBarChart,
  validateLineChart,
  validatePieChart,
  validateTableReading,
  validateScheduleReading,
} from './typeValidators/chartValidators'

import {
  validateTextHighlight,
  validateErrorSpotting,
  validateSentenceParser,
  validateCategorize,
  validateTrueFalseSeries,
  validateFillIn,
  validateVerbConjugationFlow,
  validateSentenceErrorJudge,
} from './typeValidators/languageValidators'

import {
  validateOrdering,
  validateMatching,
  validateDragDropFill,
} from './typeValidators/interactiveValidators'

import {
  getValidModesFor,
  isValidMode,
} from '../../../content/questions/questionModes'

const STAGE: PipelineStage = 1 // Stage 1b shares stage number with schema

// ─── Data Resolution Helper ───────────────────────────────────────────────────

/**
 * Resolve the effective data object for a question.
 * Supports the nested convention where data is stored under data.{typeName}.
 * E.g., data.money.{mode, targetCents} or data.clock.{mode, target}
 */
function resolveTypeData(question: Question): Record<string, unknown> {
  const data = question.data
  if (!data || typeof data !== 'object') return {}

  const typeName = question.questionType
  if (typeName) {
    // Try exact match first (e.g., data.money, data.clock)
    const nested = (data as Record<string, unknown>)[typeName]
    if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
      return nested as Record<string, unknown>
    }
    // Try camelCase fallback (e.g., step_solver → stepSolver, true_false_series → trueFalseSeries)
    const camelCase = typeName.replace(/_([a-z])/g, (_, c) => c.toUpperCase())
    if (camelCase !== typeName) {
      const camelNested = (data as Record<string, unknown>)[camelCase]
      if (
        camelNested &&
        typeof camelNested === 'object' &&
        !Array.isArray(camelNested)
      ) {
        return camelNested as Record<string, unknown>
      }
    }
  }
  return data as Record<string, unknown>
}

/**
 * Types that have comprehensive type-specific validation and should skip
 * the generic required-field check in validateDataStructure.
 * These validators handle multiple data formats (canonical + legacy).
 */
const TYPES_WITH_COMPREHENSIVE_VALIDATION: Set<string> = new Set([
  'bar_chart',
  'line_chart',
  'matching',
  'pie_chart',
  'text_highlight',
])

// ─── Main Entry Point ─────────────────────────────────────────────────────────

/**
 * Run question type validation on a question.
 * Returns ValidationIssue[] (blocker/coach/info).
 */
export function runTypeStage(question: Question): ValidationIssue[] {
  const issues: ValidationIssue[] = []
  const qId = question.id ?? 'UNKNOWN'
  const fId = question.familyId ?? 'UNKNOWN'
  const qType = question.questionType

  // ── 1. Known type check ──────────────────────────────────────────────────

  if (!qType) {
    return issues
  }

  const typeDef = QUESTION_TYPE_REGISTRY[qType as QuestionType]
  if (!typeDef) {
    issues.push(
      blocker(
        'type',
        STAGE,
        'TYPE_UNKNOWN',
        `Unknown question type: "${qType}"`,
        {
          field: 'questionType',
          actual: qType,
          questionId: qId,
          familyId: fId,
          suggestion: `Valid types: ${Object.keys(QUESTION_TYPE_REGISTRY).join(', ')}`,
        },
      ),
    )
    return issues
  }

  // ── 2. interactiveConfig deprecation check ───────────────────────────────

  if (question.interactiveConfig !== undefined) {
    issues.push(
      blocker(
        'type',
        STAGE,
        'TYPE_DEPRECATED_INTERACTIVE_CONFIG',
        'interactiveConfig is deprecated — use data.{type} convention instead',
        {
          field: 'interactiveConfig',
          questionId: qId,
          familyId: fId,
          suggestion: `Move interactive data to question.data object`,
        },
      ),
    )
  }

  // ── 3. Data structure validation for non-standard types ──────────────────

  if (typeDef.category !== 'standard') {
    const dataIssues = validateDataStructure(question, typeDef, qId, fId)
    issues.push(...dataIssues)
  }

  // ── 4. Type-specific deep validation ─────────────────────────────────────

  const deepIssues = validateTypeSpecific(question, typeDef, qId, fId)
  issues.push(...deepIssues)

  // ── 5. Mode validation (data.mode whitelist per type) ────────────────────

  const modeIssue = validateModeField(question, qId, fId)
  if (modeIssue) issues.push(modeIssue)

  return issues
}

// ─── Mode Validation ──────────────────────────────────────────────────────────

/**
 * Validate that `data.mode` (if present) is registered for this question
 * type in src/content/questions/questionModes.ts.
 *
 * Established as part of TAA Audit S0 — formal data.mode convention.
 */
function validateModeField(
  question: Question,
  qId: string,
  fId: string,
): ValidationIssue | null {
  const data = question.data as Record<string, unknown> | undefined
  if (!data || typeof data !== 'object') return null

  const mode = data.mode
  if (mode == null) return null // optional — default behaviour

  const qType = question.questionType
  if (typeof mode !== 'string') {
    return blocker(
      'type',
      STAGE,
      'TYPE_INVALID_MODE',
      `data.mode must be a string when set (got ${typeof mode})`,
      {
        field: 'data.mode',
        actual: String(mode),
        questionId: qId,
        familyId: fId,
      },
    )
  }

  const validModes = getValidModesFor(qType)
  if (!validModes) {
    return blocker(
      'type',
      STAGE,
      'TYPE_MODE_NOT_SUPPORTED',
      `data.mode is set ("${mode}") but type "${qType}" has no registered modes`,
      {
        field: 'data.mode',
        actual: mode,
        questionId: qId,
        familyId: fId,
        suggestion:
          'Remove data.mode, or register a mode for this type in src/content/questions/questionModes.ts',
      },
    )
  }

  if (!isValidMode(qType, mode)) {
    return blocker(
      'type',
      STAGE,
      'TYPE_INVALID_MODE',
      `Unknown mode "${mode}" for type "${qType}"`,
      {
        field: 'data.mode',
        actual: mode,
        expected: validModes.join(' | '),
        questionId: qId,
        familyId: fId,
        suggestion: `Valid modes for ${qType}: ${validModes.join(', ')}`,
      },
    )
  }

  return null
}

// ─── Data Structure Validation ────────────────────────────────────────────────

/**
 * Validate that non-standard types have the required data.{type} structure.
 */
function validateDataStructure(
  question: Question,
  typeDef: QuestionTypeDefinition,
  qId: string,
  fId: string,
): ValidationIssue[] {
  const issues: ValidationIssue[] = []
  const data = question.data

  if (!data || typeof data !== 'object') {
    issues.push(
      blocker(
        'type',
        STAGE,
        'TYPE_MISSING_DATA',
        `Type "${question.questionType}" (${typeDef.category}) requires a data object`,
        {
          field: 'data',
          questionId: qId,
          familyId: fId,
          suggestion: `Add a data object with required fields: ${typeDef.dataStructure?.required?.join(', ') ?? 'see type definition'}`,
        },
      ),
    )
    return issues
  }

  // Skip generic required-field check for types with comprehensive validators
  if (TYPES_WITH_COMPREHENSIVE_VALIDATION.has(question.questionType)) {
    return issues
  }

  const resolvedData = resolveTypeData(question)

  if (typeDef.dataStructure?.required) {
    for (const field of typeDef.dataStructure.required) {
      if (
        !(field in resolvedData) ||
        resolvedData[field] === undefined ||
        resolvedData[field] === null
      ) {
        issues.push(
          blocker(
            'type',
            STAGE,
            'TYPE_MISSING_DATA_FIELD',
            `Required data field "${field}" missing for type "${question.questionType}"`,
            {
              field: `data.${field}`,
              questionId: qId,
              familyId: fId,
            },
          ),
        )
      }
    }
  }

  return issues
}

// ─── Type-Specific Deep Validation (Dispatcher) ──────────────────────────────

/**
 * Run type-specific validation rules beyond structural checks.
 * Dispatches to domain-grouped validator modules.
 */
function validateTypeSpecific(
  question: Question,
  typeDef: QuestionTypeDefinition,
  qId: string,
  fId: string,
): ValidationIssue[] {
  const resolvedData = resolveTypeData(question)
  const rawData = question.data as Record<string, unknown> | undefined
  if (!resolvedData && typeDef.category === 'standard') return []
  if (!rawData) return []

  switch (question.questionType) {
    // Math types
    case 'clock':
      return validateClock(resolvedData, qId, fId)
    case 'money':
      return validateMoney(resolvedData, qId, fId)
    case 'cijferend_rekenen':
    case 'cijferend_delen':
      return validateColumnArithmetic(
        resolvedData,
        question.questionType,
        qId,
        fId,
      )
    case 'ratio_table':
      return validateRatioTable(resolvedData, qId, fId)
    case 'number_sequence':
      return validateNumberSequence(resolvedData, qId, fId)
    case 'grid_shade':
      return validateGridShade(resolvedData, qId, fId)
    case 'step_solver':
      return validateStepSolver(resolvedData, qId, fId)
    case 'comparison_judge':
      return validateComparisonJudge(resolvedData, qId, fId)
    case 'multiplication_grid':
      return validateMultiplicationGrid(resolvedData, qId, fId)
    case 'estimation_slider':
      return validateEstimationSlider(resolvedData, qId, fId)
    case 'thermometer_slider':
      return validateThermometerSlider(
        resolvedData,
        question.correctAnswer,
        qId,
        fId,
      )

    // Chart & table types
    case 'bar_chart':
      return validateBarChart(rawData, qId, fId)
    case 'line_chart':
      return validateLineChart(rawData, qId, fId)
    case 'pie_chart':
      return validatePieChart(rawData, qId, fId)
    case 'table_reading':
      return validateTableReading(resolvedData, qId, fId)
    case 'schedule_reading':
      return validateScheduleReading(resolvedData, qId, fId)

    // Language & text types
    case 'text_highlight':
      return validateTextHighlight(resolvedData, qId, fId)
    case 'error_spotting':
      return validateErrorSpotting(resolvedData, qId, fId)
    case 'sentence_parser':
      return validateSentenceParser(resolvedData, qId, fId)
    case 'categorize':
      return validateCategorize(resolvedData, qId, fId)
    case 'true_false_series':
      return validateTrueFalseSeries(resolvedData, qId, fId)
    case 'fill_in':
      return validateFillIn(question, qId, fId)
    case 'verb_conjugation_flow':
      return validateVerbConjugationFlow(resolvedData, qId, fId)
    case 'sentence_error_judge':
      return validateSentenceErrorJudge(resolvedData, qId, fId)

    // Interactive types
    case 'ordering':
      return validateOrdering(resolvedData, qId, fId)
    case 'matching':
      return validateMatching(rawData, qId, fId)
    case 'drag_drop_fill':
      return validateDragDropFill(resolvedData, qId, fId)

    default:
      return []
  }
}
